import React, { useState } from 'react';
import {
  ChevronLeft,
  Plus,
  Search,
  MoreVertical,
  Edit2,
  Archive,
  RotateCcw,
  Trash2,
  Users,
  RefreshCw,
  Percent,
  DollarSign,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useDiscounts,
  useArchiveDiscount,
  useRestoreDiscount,
  useDeleteDiscount,
} from '../../hooks/useDiscounts';
import { formatCurrency } from '@/services/tuitionService';
import type { DiscountConfig, DiscountType } from '@/types/tuition.types';
import DiscountFormModal from '../../components/tuition/DiscountFormModal';
import DiscountAssignModal from '../../components/tuition/DiscountAssignModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';

// Type metadata for display
const DISCOUNT_TYPE_META: Record<DiscountType, { label: string; icon: string; color: string }> = {
  SIBLING: { label: 'Fratrie', icon: '👨‍👩‍👧‍👦', color: 'bg-blue-100 text-blue-700' },
  MERIT: { label: 'Mérite', icon: '🏆', color: 'bg-yellow-100 text-yellow-700' },
  PARTNER: { label: 'Partenaire', icon: '🤝', color: 'bg-purple-100 text-purple-700' },
  SOCIAL: { label: 'Social', icon: '❤️', color: 'bg-red-100 text-red-700' },
  EARLY_PAYMENT: { label: 'Anticipé', icon: '⏰', color: 'bg-green-100 text-green-700' },
  STAFF_CHILD: { label: 'Personnel', icon: '👨‍💼', color: 'bg-indigo-100 text-indigo-700' },
  AMBASSADOR: { label: 'Ambassadeur', icon: '🌟', color: 'bg-orange-100 text-orange-700' },
  OTHER: { label: 'Autre', icon: '📋', color: 'bg-gray-100 text-gray-700' },
};

interface DiscountsPageProps {
  /** When true, hides the header with back button (for embedding in TuitionFeesPage) */
  embedded?: boolean;
}

const DiscountsPage: React.FC<DiscountsPageProps> = ({ embedded = false }) => {
  const navigate = useNavigate();

  // Permissions
  const { hasPermission } = usePermissions();
  const canManageDiscounts = hasPermission(Permission.FINANCE_DISCOUNT_MANAGE);
  const canDeleteDiscounts = hasPermission(Permission.FINANCE_DISCOUNT_DELETE);

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountConfig | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info' | 'archive' | 'restore';
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // Queries
  const { data, isLoading, refetch } = useDiscounts({
    includeArchived: showArchived,
    search: searchTerm || undefined,
  });

  // Mutations
  const archiveMutation = useArchiveDiscount();
  const restoreMutation = useRestoreDiscount();
  const deleteMutation = useDeleteDiscount();

  const discounts = data?.data || [];

  const handleCreateNew = () => {
    setSelectedDiscount(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (discount: DiscountConfig) => {
    setSelectedDiscount(discount);
    setIsFormModalOpen(true);
    setOpenMenuId(null);
  };

  const handleAssign = (discount: DiscountConfig) => {
    setSelectedDiscount(discount);
    setIsAssignModalOpen(true);
    setOpenMenuId(null);
  };

  const handleArchive = (discount: DiscountConfig) => {
    setOpenMenuId(null);
    setConfirmDialog({
      isOpen: true,
      title: 'Archiver la remise',
      message: `Êtes-vous sûr de vouloir archiver "${discount.name}" ? Elle ne sera plus visible par défaut mais pourra être restaurée.`,
      variant: 'archive',
      onConfirm: async () => {
        await archiveMutation.mutateAsync(discount.id);
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        refetch();
      },
    });
  };

  const handleRestore = (discount: DiscountConfig) => {
    setOpenMenuId(null);
    setConfirmDialog({
      isOpen: true,
      title: 'Restaurer la remise',
      message: `Êtes-vous sûr de vouloir restaurer "${discount.name}" ?`,
      variant: 'restore',
      onConfirm: async () => {
        await restoreMutation.mutateAsync(discount.id);
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        refetch();
      },
    });
  };

  const handleDelete = (discount: DiscountConfig) => {
    setOpenMenuId(null);
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer définitivement',
      message: `Êtes-vous sûr de vouloir supprimer définitivement "${discount.name}" ? Cette action est irréversible. Seules les remises jamais utilisées peuvent être supprimées.`,
      variant: 'danger',
      onConfirm: () => {
        // Use mutate with callbacks instead of mutateAsync to avoid console errors
        deleteMutation.mutate(discount.id, {
          onSuccess: () => {
            setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
            refetch();
          },
          onError: (error: any) => {
            // Check if error is about assigned students
            const errorMessage = error?.response?.data?.message || error?.message || '';
            if (errorMessage.includes('assigned to students') || errorMessage.includes('Archive it instead')) {
              // Show archive option dialog
              setConfirmDialog({
                isOpen: true,
                title: 'Impossible de supprimer',
                message: `Cette remise a déjà été assignée à des étudiants et ne peut pas être supprimée. Voulez-vous l'archiver à la place ? Elle restera dans l'historique mais ne sera plus active.`,
                variant: 'archive',
                onConfirm: async () => {
                  await archiveMutation.mutateAsync(discount.id);
                  setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                  refetch();
                },
              });
            } else {
              // Other error - show generic error
              setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
              alert(errorMessage);
            }
          },
        });
      },
    });
  };

  const getValueDisplay = (discount: DiscountConfig) => {
    if (discount.percentageValue !== null) {
      return (
        <div className="flex items-center gap-1">
          <Percent size={14} className="text-primary-500" />
          <span className="font-semibold text-primary-600">{discount.percentageValue}%</span>
          {discount.maxDeductionAmount !== null && (
            <span className="text-xs text-gray-500 ml-1">
              (max {formatCurrency(discount.maxDeductionAmount)})
            </span>
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1">
        <DollarSign size={14} className="text-green-500" />
        <span className="font-semibold text-green-600">{formatCurrency(discount.fixedAmount || 0)}</span>
      </div>
    );
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50/50'}>
      {/* Header - only shown when not embedded */}
      {!embedded && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/gestion-financiere/frais-scolarite')}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Remises & Bourses</h1>
                <p className="text-sm text-gray-500">
                  Configuration des remises, réductions et bourses applicables
                </p>
              </div>
            </div>
            {canManageDiscounts && (
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus size={18} />
                Nouvelle Remise
              </button>
            )}
          </div>
        </div>
      )}

      {/* Embedded Header - shown when embedded */}
      {embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Remises & Bourses</h1>
            <p className="text-gray-500 mt-1">
              Configuration des remises, réductions et bourses applicables
            </p>
          </div>
          {canManageDiscounts && (
            <button
              onClick={handleCreateNew}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus size={18} />
              Nouvelle Remise
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className={embedded ? 'bg-white rounded-xl border border-gray-200 p-4 mb-6' : 'px-6 py-4 bg-white border-b border-gray-200'}>
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher une remise ou bourse..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showArchived
                ? 'bg-gray-100 border-gray-300 text-gray-700'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {showArchived ? <Eye size={18} /> : <EyeOff size={18} />}
            {showArchived ? 'Masquer archivés' : 'Afficher archivés'}
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={embedded ? '' : 'p-6'}>
        {isLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
            <p className="mt-2 text-gray-500">Chargement des remises...</p>
          </div>
        ) : discounts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
              <Percent className="h-8 w-8 text-orange-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Aucune remise configurée</h3>
            <p className="mt-1 text-gray-500">
              {canManageDiscounts
                ? 'Commencez par créer votre première remise ou bourse'
                : 'Aucune remise configurée pour le moment'}
            </p>
            {canManageDiscounts && (
              <button
                onClick={handleCreateNew}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus size={18} />
                Créer une remise
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-visible">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remise / Bourse
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valeur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Options
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  {canManageDiscounts && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {discounts.map((discount) => {
                  const typeMeta = DISCOUNT_TYPE_META[discount.type];
                  return (
                    <tr
                      key={discount.id}
                      className={`hover:bg-gray-50 ${!discount.isActive ? 'opacity-60' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center text-lg">
                            {typeMeta.icon}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{discount.name}</p>
                            <p className="text-xs text-gray-500">{discount.code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${typeMeta.color}`}>
                          {typeMeta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">{getValueDisplay(discount)}</td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {discount._count?.studentDiscounts || 0} bénéficiaire
                          {(discount._count?.studentDiscounts || 0) > 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {discount.isRecurring && (
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                              Récurrent
                            </span>
                          )}
                          {discount.requiresApproval && (
                            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700">
                              Approbation
                            </span>
                          )}
                          {discount.isCumulative && (
                            <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                              Cumulable
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {discount.isActive ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                            Actif
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">
                            Archivé
                          </span>
                        )}
                      </td>
                      {canManageDiscounts && (
                        <td className="px-6 py-4 text-right">
                          <div className="relative inline-block">
                            <button
                              onClick={() =>
                                setOpenMenuId(openMenuId === discount.id ? null : discount.id)
                              }
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openMenuId === discount.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setOpenMenuId(null)}
                                />
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                  {discount.isActive && (
                                    <>
                                      <button
                                        onClick={() => handleEdit(discount)}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <Edit2 size={16} />
                                        Modifier
                                      </button>
                                      <button
                                        onClick={() => handleAssign(discount)}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <Users size={16} />
                                        Affecter
                                      </button>
                                      <hr className="my-1 border-gray-200" />
                                      <button
                                        onClick={() => handleArchive(discount)}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50"
                                      >
                                        <Archive size={16} />
                                        Archiver
                                      </button>
                                      {canDeleteDiscounts && (
                                        <button
                                          onClick={() => handleDelete(discount)}
                                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                          <Trash2 size={16} />
                                          Supprimer
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {!discount.isActive && (
                                    <>
                                      <button
                                        onClick={() => handleRestore(discount)}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                                      >
                                        <RotateCcw size={16} />
                                        Restaurer
                                      </button>
                                      {canDeleteDiscounts && (
                                        <button
                                          onClick={() => handleDelete(discount)}
                                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                          <Trash2 size={16} />
                                          Supprimer définitivement
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination info */}
        {data?.meta && (
          <div className="mt-4 text-sm text-gray-500 text-center">
            {data.meta.total} remise{data.meta.total > 1 ? 's' : ''} au total
          </div>
        )}
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <DiscountFormModal
          discount={selectedDiscount}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedDiscount(null);
          }}
          onSuccess={() => refetch()}
        />
      )}

      {isAssignModalOpen && selectedDiscount && (
        <DiscountAssignModal
          discount={selectedDiscount}
          onClose={() => {
            setIsAssignModalOpen(false);
            setSelectedDiscount(null);
          }}
          onSuccess={() => refetch()}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default DiscountsPage;
